import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  CUSTOMER_CATEGORY_TYPES,
  type CustomerCategory,
  type CustomerCategorySavePayload,
  type CustomerCategoryType,
} from '@/types/customer-category'

interface CustomerCategoryDialogProps {
  open: boolean
  category: CustomerCategory | null
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: CustomerCategorySavePayload) => void
}

export function CustomerCategoryDialog({
  open,
  category,
  isSaving,
  onOpenChange,
  onSubmit,
}: CustomerCategoryDialogProps) {
  const [name, setName] = useState('')
  const [type, setType] = useState<string>('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (!open) return

    setName(category?.name ?? '')
    setType(category ? String(category.type) : '')
    setDescription(category?.description ?? '')
  }, [category, open])

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('请输入类型名称')
      return
    }
    if (type === '') {
      toast.error('请选择类型种类')
      return
    }

    onSubmit({
      name: name.trim(),
      type: Number(type) as CustomerCategoryType,
      description: description.trim(),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{category ? '编辑客户类型' : '新建客户类型'}</DialogTitle>
          <DialogDescription>维护客户类型名称、类型种类和内部备注。</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="customer-category-name">名称</Label>
            <Input
              id="customer-category-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="例如：VIP在期"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer-category-type">类型种类</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="customer-category-type" aria-label="类型种类">
                <SelectValue placeholder="选择类型种类" />
              </SelectTrigger>
              <SelectContent>
                {CUSTOMER_CATEGORY_TYPES.map((item) => (
                  <SelectItem key={item.value} value={String(item.value)}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer-category-description">备注</Label>
            <Textarea
              id="customer-category-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="内部备注，可留空"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSaving}>
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
